import {CatalogItemSelectStateVO, LayerSettingsApi, TopicTreeNodeVO} from '../../api';

import React, {Dispatch, MutableRefObject, SetStateAction, useCallback, useEffect, useMemo, useState} from 'react';

import {DndProvider} from 'react-dnd';
import {Tree, NodeModel, MultiBackend, getBackendOptions, DropOptions} from '@minoru/react-dnd-treeview';
import {CustomNode} from './CustomNode';
import {Placeholder} from './Placeholder';
import {CustomDragPreview} from './CustomDragPreview';
import {debounce} from 'lodash-es';
import {useQueryClient} from 'react-query';
import {CACHE_KEY_LAYER_TOPIC} from '../../queryKeys';
import {actionApiCall} from '@mc/react-app-v4';
import {LayerId, MapEditorActions} from '@mc/react-mapio';
import {Modal, NoData} from '@mc/ui-v4';
import {ApiCallWaitingModal} from '../modals/ApiCallWaitingModal';
import {CACHE_KEY_TOPICS_MAP, useAtlasPristupnosti} from '../../hooks/useTopicsMap';

/*
Komponenta stromu s podporou drag and drop, Lidl verze prozatím zde. Vymazlená později přijde do kitu.
 */
type Props = {
    data: TopicTreeNodeVO[];
    selectedMenuItemId: number;
    setSelectedMenuItemId: Dispatch<SetStateAction<number | undefined>>;
    topicId: string;
    setTopicOnDelete: () => void;
    mapRef: MutableRefObject<MapEditorActions>;
    layersIsVlastnik: number[];
    onLayerDelete: (layerId: LayerId) => void;
};
export type restData = {
    catalogItemId: number;
    selected: boolean;
    layerId?: number;
    topicId?: string;
};
type SelectionItem = {
    id: string;
    selected: boolean;
};
const TopicTreeMenu: React.FC<Props> = ({
    data,
    topicId,
    setTopicOnDelete,
    selectedMenuItemId,
    setSelectedMenuItemId,
    mapRef,
    layersIsVlastnik,
    onLayerDelete,
}) => {
    const [selectedNodes, setSelectedNodes] = useState([]);
    const [treeData, setTreeData] = useState<NodeModel<restData>[]>([]);
    const allNodeIds = useMemo(() => treeData.map(node => node.id), [treeData]);
    const atlasPristupnosti = useAtlasPristupnosti();

    useEffect(() => {
        const tempNodeModel: NodeModel<restData>[] = data.map((item: TopicTreeNodeVO) => ({
            id: item.id,
            parent: item.parentId ?? 0,
            text: item.text ?? '-',
            droppable: !item.layerId,
            data: {
                catalogItemId: item.catalogItemId,
                selected: item.selected,
                layerId: item.layerId,
                topicId: item.topicId,
            },
        }));

        setSelectedNodes(tempNodeModel.filter(item => item.data?.selected));
        setTreeData(tempNodeModel);
    }, [data]);

    useEffect(() => {
        if (!selectedMenuItemId) {
            const topicTreeItem = treeData.find(item => item?.data?.topicId);
            setSelectedMenuItemId(topicTreeItem?.data?.catalogItemId ?? null);
        }
    }, [treeData, selectedMenuItemId]);

    const queryClient = useQueryClient();
    const compareTrees = (newTree: NodeModel<restData>[], oldTree: NodeModel<restData>[]): CatalogItemSelectStateVO[] => {
        const newTreeMap = new Map(newTree.map(node => [node.id.toString(), node]));
        const oldTreeMap = new Map(oldTree.map(node => [node.id.toString(), node]));

        // Find nodes that only exist in the new tree
        const onlyInNewTree = newTree.filter(node => !oldTreeMap.has(node.id.toString()) && node.data.catalogItemId !== null);

        // Find nodes that only exist in the old tree
        const onlyInOldTree = oldTree.filter(node => !newTreeMap.has(node.id.toString()) && node.data.catalogItemId !== null);

        // Return new array
        return [
            ...onlyInNewTree.map(node => ({id: node.data?.catalogItemId, selected: true})),
            ...onlyInOldTree.map(node => ({id: node.data?.catalogItemId, selected: false})),
        ];
    };
    const debouncedInvalidate = useCallback(
        debounce(() => {
            queryClient.invalidateQueries([CACHE_KEY_LAYER_TOPIC, topicId]);
        }, 1000), // Debounce time
        [topicId, queryClient], // Dependencies
    );

    useEffect(() => {
        // Cleanup
        return () => {
            debouncedInvalidate.cancel();
        };
    }, [debouncedInvalidate]);

    const handleOrderChange = useCallback(
        (newTree: NodeModel<restData>[], options: DropOptions<restData>) => {
            const newSameLevelNodes: number[] = newTree.filter(item => item.parent === options.dropTargetId).map(item => item.data.catalogItemId);
            const oldSameLevelNodes: number[] = newTree.filter(item => item.parent === options.dragSource.parent).map(item => item.data.catalogItemId);
            const newParentNodeData = newTree.find(item => item.id === options.dropTargetId).data;
            const oldParentNodeData = newTree.find(item => item.id === options.dragSource.parent).data;

            const newParentNodeId = newParentNodeData.topicId ? newParentNodeData.topicId : newParentNodeData.catalogItemId;
            const oldParentNodeId = oldParentNodeData.topicId ? oldParentNodeData.topicId : oldParentNodeData.catalogItemId;

            // Kontrola, zda došlo ke změně
            const isSameOrder =
                newSameLevelNodes.length === oldSameLevelNodes.length && newSameLevelNodes.every((value, index) => value === oldSameLevelNodes[index]);

            if (!isSameOrder) {
                actionApiCall(async () => {
                    let changeOrderData = {};

                    if (newParentNodeId === oldParentNodeId) {
                        changeOrderData = {
                            taker: {
                                parentTopicId: newParentNodeData.topicId,
                                parentCatalogId: newParentNodeData.catalogItemId,
                                childrenIds: newSameLevelNodes,
                                isFirstLevel: !!newParentNodeData.topicId,
                            },
                        };
                    } else {
                        changeOrderData = {
                            taker: {
                                parentTopicId: newParentNodeData.topicId,
                                parentCatalogId: newParentNodeData.catalogItemId,
                                childrenIds: newSameLevelNodes,
                                isFirstLevel: !!newParentNodeData.topicId,
                            },
                            giver: {
                                parentTopicId: oldParentNodeData.topicId,
                                parentCatalogId: oldParentNodeData.catalogItemId,
                                childrenIds: oldSameLevelNodes,
                                isFirstLevel: !!oldParentNodeData.topicId,
                            },
                        };
                    }
                    await LayerSettingsApi.changeChildrenOrder({
                        ...changeOrderData,
                    });
                    setTreeData(newTree);
                });
            }
        },
        [debouncedInvalidate],
    );
    const handleCheck = (node: NodeModel<restData>) => {
        // Vytvoříme kopii vybraných uzlů
        let updatedSelectedNodes: NodeModel<restData>[] = [...selectedNodes];

        // Funkce pro rekurzivní výběr uzlů
        const recursiveSelection = (n: NodeModel<restData>, selected: boolean) => {
            // Najdeme index aktuálního uzlu v seznamu vybraných uzlů
            const itemIndex = updatedSelectedNodes.findIndex(item => item.id === n.id);
            // Najdeme všechny uzly, které mají tento uzel jako rodiče a aplikujeme na ně rekurzivní výběr
            const childNodes = treeData.filter(item => item.parent === n.id);
            // Zjistíme, zda jsou v uzlu vybraní alespoň nekteří potomci a zároveň ne všichni
            const indeterminate = calculateIndeterminate(n);

            // Pokud uzel není v seznamu vybraných, přidáme ho. Jinak ho odstraníme
            if (itemIndex === -1) {
                updatedSelectedNodes = [...updatedSelectedNodes, n];
            } else {
                updatedSelectedNodes = updatedSelectedNodes.filter(item => item.id !== n.id);
            }

            if (childNodes.length > 0 && selected && !indeterminate) {
                updatedSelectedNodes = [...updatedSelectedNodes, ...childNodes];
                childNodes.forEach(childNode => recursiveSelection(childNode, true));
            } else {
                updatedSelectedNodes = updatedSelectedNodes.filter(item => item.parent !== n.id);
                childNodes.forEach(childNode => recursiveSelection(childNode, false));
            }
        };

        // Zahájíme rekurzivní výběr s vybraným stavem rodičovského uzlu
        recursiveSelection(
            node,
            selectedNodes.some(item => item.id === node.id),
        );

        // Porovnáme stromy a vytvoříme seznam změn pro API
        const changeListForApi = compareTrees(updatedSelectedNodes, selectedNodes);

        // Zavoláme API pro aktualizaci stavů výběru a aktualizaci tématu
        actionApiCall(async () => {
            await Modal.showWaiting(
                actionApiCall(async (): Promise<any> => {
                    await LayerSettingsApi.updateSelectStates(topicId, changeListForApi);
                    mapRef.current.updateTopic(topicId);
                }),
                ApiCallWaitingModal,
            );
            await queryClient.invalidateQueries([CACHE_KEY_TOPICS_MAP]);
        });

        // Aktualizujeme stav vybraných uzlů
        setSelectedNodes(updatedSelectedNodes);

        // Aktualizujeme treeData pro správné zobrazení checkboxů
        const updatedTreeData = treeData.map(node => {
            const correspondingNode = updatedSelectedNodes.find(selectedNode => selectedNode.id === node.id);
            if (correspondingNode) {
                return {...node, data: {...node.data, selected: true}};
            } else {
                return {...node, data: {...node.data, selected: false}};
            }
        });

        // Aktualizujeme stav treeData
        setTreeData(updatedTreeData);
    };

    const calculateIndeterminate = (node: NodeModel<restData>): boolean => {
        const childNodes = treeData.filter(item => item.parent === node.id);
        return childNodes.length > 0 && !childNodes.every(item => item.data?.selected) && childNodes.some(item => item.data?.selected);
    };

    /**
     * Posun pozic node v rámci skupiny.
     * @param node NodeModel<restData> - Node, který má být přesunut .
     * @param atIndex number - Cílový index, na který má být node přesunut.
     */
    const moveItem = (node: NodeModel<restData>, atIndex: number) => {
        const index = treeData.findIndex(item => item.id === node.id);

        if (index === -1) return;

        const sameParentItems = treeData.filter(item => item.parent === node.parent);
        const sameParentIndex = sameParentItems.findIndex(item => item.id === node.id);

        if (atIndex > sameParentIndex && index < treeData.length - 1) {
            [treeData[index], treeData[index + 1]] = [treeData[index + 1], treeData[index]];
        } else if (atIndex < sameParentIndex && index > 0) {
            [treeData[index], treeData[index - 1]] = [treeData[index - 1], treeData[index]];
        }

        actionApiCall(async () => {
            let changeOrderData = {};

            const newSameLevelNodes: number[] = treeData.filter(item => item.parent === node.parent).map(item => item.data.catalogItemId);
            const oldSameLevelNodes: number[] = treeData.filter(item => item.parent === node.parent).map(item => item.data.catalogItemId);
            const newParentNodeData = treeData.find(item => item.id === node.parent).data;
            const oldParentNodeData = treeData.find(item => item.id === node.parent).data;

            const newParentNodeId = newParentNodeData.topicId ? newParentNodeData.topicId : newParentNodeData.catalogItemId;
            const oldParentNodeId = oldParentNodeData.topicId ? oldParentNodeData.topicId : oldParentNodeData.catalogItemId;

            if (newParentNodeId === oldParentNodeId) {
                changeOrderData = {
                    taker: {
                        parentTopicId: newParentNodeData.topicId,
                        parentCatalogId: newParentNodeData.catalogItemId,
                        childrenIds: newSameLevelNodes,
                        isFirstLevel: !!newParentNodeData.topicId,
                    },
                };
            } else {
                changeOrderData = {
                    taker: {
                        parentTopicId: newParentNodeData.topicId,
                        parentCatalogId: newParentNodeData.catalogItemId,
                        childrenIds: newSameLevelNodes,
                        isFirstLevel: !!newParentNodeData.topicId,
                    },
                    giver: {
                        parentTopicId: oldParentNodeData.topicId,
                        parentCatalogId: oldParentNodeData.catalogItemId,
                        childrenIds: oldSameLevelNodes,
                        isFirstLevel: !!oldParentNodeData.topicId,
                    },
                };
            }

            await LayerSettingsApi.changeChildrenOrder({
                ...changeOrderData,
            });

            setTreeData([...treeData]);
        });
    };

    const areAllChildNodesInLayersIsVlastnik = (node: NodeModel<restData>, treeData: NodeModel<restData>[], layersIsVlastnik: number[]) => {
        const childNodes = treeData.filter(item => item.parent.toString() === node.id.toString());

        if (childNodes.length === 0) {
            if (node.parent === 0 || !node.data?.layerId) return true;
            return layersIsVlastnik.includes(node.data?.layerId);
        }

        return childNodes.every(child => {
            if (child.data?.catalogItemId) {
                // Pokud child má potomky, rekurzivně zkontrolujeme, zda se všechny nacházejí ve vlastníkovo vrstvách
                return areAllChildNodesInLayersIsVlastnik(child, treeData, layersIsVlastnik);
            }
            if (!layersIsVlastnik.includes(child.data?.layerId)) {
                return true;
            }
            return false;
        });
    };
    if (treeData.length === 0) {
        return <NoData>Katalog vrstev se nenačetl správně</NoData>;
    }

    return (
        <DndProvider backend={MultiBackend} options={getBackendOptions()}>
            <div className={'h-100 config-menu-overflow'}>
                <Tree
                    tree={treeData}
                    initialOpen={allNodeIds}
                    rootId={0}
                    render={(node, {depth, isOpen, onToggle}) => {
                        const childNodes = treeData.filter(item => item.parent === node.id);
                        const indeterminate = calculateIndeterminate(node);
                        const isUserVlastnik = areAllChildNodesInLayersIsVlastnik(node, treeData, layersIsVlastnik);
                        return (
                            <CustomNode
                                handleSelect={(node: number) => setSelectedMenuItemId(node ?? null)}
                                isSelected={selectedMenuItemId === node.data?.catalogItemId}
                                isChecked={
                                    (childNodes.length === 0 && selectedNodes.some(n => n.id === node.id)) ||
                                    (childNodes.length > 0 && childNodes.every(item => item.data?.selected))
                                }
                                indeterminate={indeterminate}
                                handleCheck={handleCheck}
                                node={node}
                                depth={depth}
                                treeData={treeData}
                                moveItem={moveItem}
                                isOpen={isOpen}
                                topicId={topicId}
                                mapRef={mapRef}
                                isUserVlastnik={isUserVlastnik}
                                setTopicOnDelete={setTopicOnDelete}
                                onToggle={onToggle}
                                onLayerDelete={onLayerDelete}
                                atlasPristupnosti={atlasPristupnosti}
                            />
                        );
                    }}
                    canDrag={node => node.parent !== 0}
                    dragPreviewRender={monitorProps => <CustomDragPreview monitorProps={monitorProps} />}
                    onDrop={handleOrderChange}
                    classes={{
                        root: 'treeRoot',
                        draggingSource: 'draggingSource',
                        placeholder: 'placeholderContainer',
                    }}
                    sort={false}
                    insertDroppableFirst={false}
                    canDrop={(tree, {dragSource, dropTargetId /*, dropTarget*/}) => {
                        if (dragSource?.parent === dropTargetId) {
                            return true;
                        }
                    }}
                    dropTargetOffset={10}
                    placeholderRender={(node, {depth}) => <Placeholder node={node} depth={depth} />}
                />
            </div>
        </DndProvider>
    );
};

export default TopicTreeMenu;
